import React, { useState, useEffect, useRef, useMemo, createContext } from "react";
import { crud, url } from "../crudRequests";
import { useNavigate } from "react-router-dom";
import io from "socket.io-client";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Avatar,
  Divider,
  Heading,
  Badge,
  Spinner,
  Icon,
  useToast,
  Button,
  IconButton,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
} from "@chakra-ui/react";
import { IoSearch, IoChevronDown, IoChevronForward } from "react-icons/io5";
import { FiHash, FiArrowLeft, FiPlus } from "react-icons/fi";
import { BsChatDots } from "react-icons/bs";
import { Collapse, useDisclosure } from "@chakra-ui/react";
import StaffChatArea from "./StaffChatArea";
import { createOutbox } from "../features/messaging/outbox";
import { patientMessagingApi } from "../features/messaging/api";
import CSSAvatar from "./CSSAvatar";
import GroupManagementModal from "./GroupManagementModal";
import {
  canPatientInitiateNewConversation,
  canPatientRespondToConversation,
  canPatientViewConversation,
  shouldAdoptCreatedConversation,
} from "./patientMessagingPermissions";
import {
  patientConversationsKey,
  useInvalidatePatientConversations,
  useInvalidatePatientUnread,
  useMarkConversationRead,
  usePatientConversations,
  usePatientUnreadSummary,
} from "../features/messaging/hooks";
import {
  INCOMING_MESSAGE_ACTIONS,
  formatBadgeCount,
  incomingMessageAction,
  isMessagingBadgeEnabled,
  unreadCountFor,
  unreadMapFrom,
} from "../features/messaging/unread";
import { memberRefOf } from "../features/messaging/api";
import {chatSocketOptions, onChatRefusal} from "../features/messaging/chatHandshake";

// Context for sharing state between components
export const MessagingContext = createContext();
export const PatientContext = createContext();
export const GroupManagementContext = createContext();

// The first member that is not this patient: the person a direct row is shown as.
function otherMemberOf(conv, patientId) {
  const members = Array.isArray(conv?.members) ? conv.members : [];
  return members.find((member) => !(member.kind === "patient" && member.id === patientId)) || null;
}

function conversationLabel(conv, patientId) {
  if (conv?.kind === "group") return conv.title || "Unnamed Group";
  return otherMemberOf(conv, patientId)?.name || "Unknown";
}

// Viewable rows, groups first, then the latest message first (no message last).
function listRowsFrom(views, patientId) {
  return (views || [])
    .filter((conv) => canPatientViewConversation(conv, patientId))
    .sort((a, b) => {
      const aGroup = a.kind === "group";
      const bGroup = b.kind === "group";
      if (aGroup !== bGroup) return aGroup ? -1 : 1;
      return (b.lastMessageAt ?? -1) - (a.lastMessageAt ?? -1);
    });
}

export default function PatientChat({ patient, dashState, dispatch }) {
  const navigate = useNavigate();
  const toast = useToast();
  
  // State management for patient messaging
  const [selectedConversation, setSelectedConversation] = useState(null);
  // Which thread the chat area shows; a draft has no id, so it is keyed by its target.
  const [threadKey, setThreadKey] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Group management modal state
  const [showGroupManagementModal, setShowGroupManagementModal] = useState(false);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [availableDoctors, setAvailableDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [patientCanInitiate, setPatientCanInitiate] = useState(
    canPatientInitiateNewConversation(patient)
  );
  const [managedGroup, setManagedGroup] = useState(null);
  
  // Socket reference
  const socketRef = useRef(null);
  const isSocketSetup = useRef(false);
  const selectedConversationRef = useRef(null);
  const conversationsRef = useRef([]);
  // Unread counts come from the server; the portal never counts them itself.
  const messagingAuth = useMemo(() => ({ jwt: dashState?.jwt }), [dashState?.jwt]);
  const messagingAuthRef = useRef(messagingAuth);
  messagingAuthRef.current = messagingAuth;
  // What the patient sent that the server has not stored yet (ADR 0018 §5):
  // one outbox for the chat page, so a message on its way survives switching
  // conversations. The open thread shows its own (StaffChatArea).
  const storedListenersRef = useRef(new Set());
  const outbox = useMemo(
    () =>
      createOutbox({
        send: (entry) => patientMessagingApi.sendMessage(messagingAuthRef.current, entry),
        onStored: (conversationId, message) => {
          for (const listener of storedListenersRef.current) listener(conversationId, message);
        },
      }),
    [patient?.pid]
  );
  const unreadSummary = usePatientUnreadSummary(messagingAuth, {
    patientId: patient?.pid,
    enabled: isMessagingBadgeEnabled(dashState),
  });
  const unreadByConversation = useMemo(
    () => unreadMapFrom(unreadSummary.data),
    [unreadSummary.data]
  );
  const markConversationRead = useMarkConversationRead(messagingAuth, patient?.pid);
  const invalidateUnread = useInvalidatePatientUnread(patient?.pid);
  const conversationsQuery = usePatientConversations(messagingAuth, {
    patientId: patient?.pid,
    enabled: !!patient?.pid && !!dashState?.jwt,
  });
  const refreshConversations = useInvalidatePatientConversations(patient?.pid);
  const queryClient = useQueryClient();
  const conversations = useMemo(
    () => listRowsFrom(conversationsQuery.data, patient?.pid),
    [conversationsQuery.data, patient?.pid]
  );
  const filteredConversations = useMemo(() => {
    const term = searchTerm.toLowerCase();
    if (!term) return conversations;
    return conversations.filter((conv) => conversationLabel(conv, patient?.pid).toLowerCase().includes(term));
  }, [searchTerm, conversations, patient?.pid]);
  const loading = conversationsQuery.isLoading;
  // Socket listeners are registered once; they reach the latest render through these refs.
  const readOpenConversationRef = useRef(null);
  const incomingMessageRef = useRef(null);
  const conversationChangedRef = useRef(null);

  // Collapsible sections
  const { isOpen: isGroupsOpen, onToggle: onGroupsToggle } = useDisclosure({ defaultIsOpen: true });
  const { isOpen: isDMsOpen, onToggle: onDMsToggle } = useDisclosure({ defaultIsOpen: true });
  
  // Separate groups and DMs
  const groups = filteredConversations.filter(c => c.kind === "group");
  const dms = filteredConversations.filter(c => c.kind !== "group");
  
  // A user selection (row click, start chat, back) is the only thing that re-keys the thread.
  const selectConversation = (conv) => {
    setSelectedConversation(conv);
    setThreadKey(
      conv
        ? conv.conversationId || `draft:${conv.draftTarget.kind}:${conv.draftTarget.id}`
        : null
    );
  };

  // MP-3 creates the conversation on the first send and hands the server row back:
  // it replaces the open draft in place (same key, no remount).
  const handleConversationCreated = (conversation) => {
    if (shouldAdoptCreatedConversation(selectedConversationRef.current, conversation)) {
      setSelectedConversation(conversation);
    }
    socketRef.current?.emit("join-room", conversation.conversationId);
    refreshConversations();
  };

  // Open group management modal
  const openGroupManagement = (group) => {
    setManagedGroup(group);
    setShowGroupManagementModal(true);
  };
  
  // Handle leave group
  const handleLeaveGroup = (groupId) => {
    // Clear selection if this was the selected conversation
    if (selectedConversation?.conversationId === groupId) {
      selectConversation(null);
    }

    // Reload conversations to sync with backend
    refreshConversations();
  };
  
  // Keep ref in sync
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  // Read is what was shown (ADR 0018 §5): the open thread is marked read on
  // open, when its window comes back in front, and on every message that
  // arrives into it, only while its tab is visible and its window focused, and
  // up to the latest message shown (`upTo`). Never for a draft 1:1 that has not
  // been stored yet (it is not listed).
  const shownToThePatient = () =>
    document.visibilityState === "visible" && (typeof document.hasFocus !== "function" || document.hasFocus());
  readOpenConversationRef.current = (conversationId) => {
    if (!conversationId || !shownToThePatient()) return;
    const row = conversationsRef.current.find((conv) => conv.conversationId === conversationId);
    if (!row) return;
    markConversationRead.mutate({ conversationId, upTo: Number.isSafeInteger(row.lastMessageAt) ? row.lastMessageAt : null });
  };

  incomingMessageRef.current = (messageData) => {
    const action = incomingMessageAction({
      messageData,
      patientId: patient?.pid,
      openConversationId: selectedConversationRef.current?.conversationId ?? null,
      isVisible: shownToThePatient(),
    });
    if (action === INCOMING_MESSAGE_ACTIONS.MARK_READ) {
      const upTo = Number.isSafeInteger(messageData.createdAtMs) ? messageData.createdAtMs : null;
      markConversationRead.mutate({ conversationId: messageData.conversationId, upTo });
    } else if (action === INCOMING_MESSAGE_ACTIONS.REFETCH) {
      invalidateUnread();
    }
  };

  // The server tells the patient a conversation changed (created, members, role,
  // left, archived, cleared, message_deleted); the list is the source of truth,
  // so refetch it and react to what the new list no longer holds.
  conversationChangedRef.current = (payload) => {
    if (typeof payload?.conversationId !== "string") return;
    const { conversationId, reason } = payload;
    if (reason === "created" || reason === "members") {
      socketRef.current?.emit("join-room", conversationId);
    }
    const listedBefore = new Set(conversationsRef.current.map((conv) => conv.conversationId));
    const openId = selectedConversationRef.current?.conversationId ?? null;
    Promise.resolve(refreshConversations()).then(() => {
      const after = listRowsFrom(queryClient.getQueryData(patientConversationsKey(patient?.pid)), patient?.pid);
      const added = after.find((conv) => !listedBefore.has(conv.conversationId));
      if (added && added.kind === "group") {
        toast({
          title: "Added to group",
          description: `You've been added to "${added.title || "Unnamed Group"}"`,
          status: "info",
          duration: 4000,
          isClosable: true,
        });
      }
      if (openId && !after.some((conv) => conv.conversationId === openId)) {
        setSelectedConversation(null);
        if (reason === "members") {
          toast({
            title: "Removed from group",
            description: "You have been removed from the group",
            status: "warning",
            duration: 4000,
          });
        } else if (reason === "archived") {
          toast({
            title: "Group deleted",
            description: "A group you were part of has been deleted",
            status: "warning",
            duration: 4000,
            isClosable: true,
          });
        }
      } else if (reason === "cleared" && openId === conversationId) {
        toast({
          title: "Chat cleared",
          description: "An admin has cleared all messages in this chat",
          status: "info",
          duration: 4000,
          isClosable: true,
        });
      }
    });
  };

  useEffect(() => {
    readOpenConversationRef.current?.(selectedConversation?.conversationId);
  }, [selectedConversation?.conversationId]);

  useEffect(() => {
    const onShown = () => readOpenConversationRef.current?.(selectedConversationRef.current?.conversationId);
    document.addEventListener("visibilitychange", onShown);
    window.addEventListener("focus", onShown);
    return () => {
      document.removeEventListener("visibilitychange", onShown);
      window.removeEventListener("focus", onShown);
    };
  }, []);

  // Setup socket ONCE
  useEffect(() => {
    if (!patient?.pid || isSocketSetup.current) return;
    
    // Mark as setup to prevent multiple connections
    isSocketSetup.current = true;
    
    // Connect to chat namespace; the session is the patient cookie (D8)
    const socket = io(url + "/chat", chatSocketOptions({rooms: [`user_${patient.pid}`], userId: patient.pid}));

    socketRef.current = socket;

    // A refused handshake (no session cookie, or an account that may no longer
    // hold one) is never retried, so say so once instead of leaving the list
    // silently frozen.
    onChatRefusal(socket, () => {
      if (toast.isActive("chat-socket-refused")) return;
      toast({
        id: "chat-socket-refused",
        title: "Messages are not updating",
        description: "Refresh the page to reconnect.",
        status: "warning",
        duration: null,
        isClosable: true,
      });
    });

    // Rooms are joined when the list loads, which can happen before this socket
    // connects. Join them again on every (re)connect so new messages keep
    // reaching the sidebar and its badges.
    socket.on("connect", () => {
      conversationsRef.current.forEach((conv) => {
        socket.emit("join-room", conv.conversationId);
      });
    });

    // Listen for incoming messages to update sidebar
    socket.on("message", (messageData) => {
      queryClient.setQueryData(patientConversationsKey(patient.pid), (rows) =>
        Array.isArray(rows)
          ? rows.map((row) => {
              if (row.conversationId !== messageData.conversationId) return row;
              const sentAt = Date.parse(messageData.timestamp);
              return {
                ...row,
                lastMessage: messageData,
                lastMessageAt: Number.isNaN(sentAt) ? row.lastMessageAt : sentAt,
              };
            })
          : rows
      );
      incomingMessageRef.current?.(messageData);
    });

    // Listen for conversation changes the server pushes to the patient's room
    socket.on("conversation:changed", (payload) => {
      conversationChangedRef.current?.(payload);
    });

    
    // Cleanup function
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        isSocketSetup.current = false;
      }
    };
  }, [patient?.pid]);
  
  // Load patient conversations
  // Debug: Check patient messaging permissions

  
  // Handle new chat button click
  const handleNewChat = async () => {
    if (!patientCanInitiate) return;

    setLoadingDoctors(true);
    
    try {
   // Filter out staff who already have conversations with the patient
   let existingConvIds = conversations
   .filter(conv => conv.kind !== "group")
   .flatMap(conv => conv.members.filter(member => member.kind !== "patient").map(member => member.id))

    existingConvIds = [...new Set(existingConvIds)];
    const patientDoctorIds = Array.isArray(patient.did) ? patient.did : (patient.did ? [patient.did] : []);


      // Fetch all doctors and admins
      const requests = [
        {
          db: dashState.db,
          collection: "doctors",
          parameters: [{did: {$nin: existingConvIds, $in: patientDoctorIds}}],
          method: "find",
        },
        {
          db: dashState.db,
          collection: "admins",
          parameters: [{id: {$nin: existingConvIds}}],
          method: "find",
        }
      ];
      
      const responses = await crud(dashState, requests);
      
      // Process all doctors
      const allDoctors = responses.data?.[0] || [];
      
      const assignedDoctors = allDoctors.map(doc => {
        const docName = doc.name || `${doc.fName || ''} ${doc.lName || ''}`.trim() || 'Clinician';
      return {
          id: doc.did,
          name: docName,
          userType: 'doctor',
          photo: doc.photo,
          specialty: doc.specialty || 'General Practice',
          isAssigned: true,
          isPrimary: patient.preferredClinicianId === doc.did
        };
        

      });
      
      // Process admins - they can always be contacted
      const adminsList = responses.data?.[1] || [];
      const admins = adminsList.map(admin => {
        const adminName = admin.name || `${admin.fName || ''} ${admin.lName || ''}`.trim() || 'Admin';
        return {
          id: admin.id || admin._id,
          name: adminName,
          userType: 'admin',
          photo: admin.photo,
        };
      });
      
    
   
      // Combine only assigned doctors and admins (NO other doctors)
      const availableStaff = [...assignedDoctors, ...admins]
      
      setAvailableDoctors(availableStaff);
      setShowNewChatModal(true);
      
    } catch (error) {
      console.error("Error loading available staff:", error);
      toast({
        title: "Error loading contacts",
        description: "Could not load available staff members",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoadingDoctors(false);
    }
  };
  
  // Handle starting a new conversation: open the listed 1:1 or a draft row.
  // The server mints the id on the first send; nothing is stored before that.
  const handleStartConversation = (staffMember) => {
    if (!patientCanInitiate) return;

    setShowNewChatModal(false);
    const {id, name, photo, userType} = staffMember;

    let ref;
    try {
      ref = memberRefOf({id, userType});
    } catch (error) {
      console.error("Error starting conversation:", error);
      toast({
        title: "Could not open conversation",
        description: "Please try again",
        status: "error",
        duration: 3000,
      });
      return;
    }

    const listed = conversationsRef.current.find(
      (conv) =>
        conv.kind === "direct" &&
        conv.members.some((member) => member.kind === ref.kind && member.id === ref.id)
    );
    if (listed) {
      selectConversation(listed);
      return;
    }

    selectConversation({
      conversationId: null,
      kind: "direct",
      title: null,
      members: [
        {
          kind: "patient",
          id: patient.pid,
          name: `${patient.fName || ""} ${patient.lName || ""}`.trim(),
          photo: patient.photo || null,
        },
        {kind: ref.kind, id: ref.id, name, photo: photo || null},
      ],
      draftTarget: {kind: ref.kind, id: ref.id},
    });
  };

  // Keep conversationsRef in sync with conversations state
  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);
  
  // Keep the open server row current when the list shows new members or a new title.
  useEffect(() => {
    const open = selectedConversationRef.current;
    if (!open?.conversationId) return;
    const current = conversations.find((conv) => conv.conversationId === open.conversationId);
    if (current && (current.members.length !== open.members?.length || current.title !== open.title)) {
      setSelectedConversation(current);
    }
  }, [conversations]);

  // Join every listed room for real-time updates after each list load
  useEffect(() => {
    if (socketRef.current?.connected) {
      conversations.forEach((conv) => socketRef.current.emit("join-room", conv.conversationId));
    }
  }, [conversations]);

  // Keep selectedConversationRef in sync
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);
  
  // Load patient settings including messaging permissions
  const loadPatientSettings = async () => {
    try {
      const response = await crud(dashState, [
        {
          db: dashState.db,
          collection: "patients",
          parameters: [{ pid: patient.pid }],
          method: "findOne",
        }
      ]);
      
      if (response.data?.[0]) {
        const updatedPatient = response.data[0];
        
        // Update local state
        setPatientCanInitiate(canPatientInitiateNewConversation(updatedPatient));
        
        // Update patient in parent state if dispatch is available
        if (dispatch) {
          dispatch({
            type: "SET_PATIENT", 
            payload: updatedPatient
          });
        }
      }
    } catch (error) {
      console.error("Error loading patient settings:", error);
    }
  };

  // Load the latest patient settings to get messaging permissions
  useEffect(() => {
    if (patient?.pid && dashState?.jwt) {
      loadPatientSettings();
    }
  }, [patient?.pid, dashState?.jwt]);
  
  return (
    <Flex w="100%" h="100%">
      {/* Sidebar */}
      <Box
        w={{ base: selectedConversation ? "0" : "100%", md: "280px" }}
        minW={{ md: "280px" }}
        h="100%"
        borderRight="1px solid"
        borderColor="gray.100"
        bg="white"
        display={{ base: selectedConversation ? "none" : "flex", md: "flex" }}
        flexDirection="column"
      >
        {/* Header */}
        <Box px={4} pt={5} pb={4} borderBottom="1px solid" borderColor="gray.100">
          <Box mb={4}>
            <Text fontSize="lg" fontWeight="600" color="#1a1f36">
              Messages
            </Text>
            <Text fontSize="12px" color="#6b7280" mt={0.5}>
              {patient?.fName} {patient?.lName}
            </Text>
          </Box>

          {/* Search Bar */}
          <InputGroup
            size="sm"
            mb={0}
            sx={{
              '& .search-icon': {
                color: 'gray.300',
                transition: 'color 0.2s'
              },
              '&:focus-within .search-icon': {
                color: 'gray.500'
              }
            }}
          >
            <InputLeftElement pointerEvents="none" h="34px">
              <IoSearch className="search-icon" />
            </InputLeftElement>
            <Input
              placeholder="Search conversations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              bg="gray.50"
              border="1px solid"
              borderColor="gray.100"
              fontSize="13px"
              borderRadius="lg"
              h="34px"
              pl="36px"
              _placeholder={{ color: "gray.400" }}
              _hover={{
                borderColor: "gray.200",
              }}
              _focus={{
                borderColor: "#5754FF",
                bg: "white",
                boxShadow: "0 0 0 1px #5754FF"
              }}
            />
          </InputGroup>
        </Box>
        
        {/* Conversations List */}
        <VStack
          flex={1}
          overflowY="auto"
          spacing={0}
          align="stretch"
          bg="white"
          px={2}
          py={2}
          className="sbar3"
        >
          {loading ? (
            <Flex justify="center" align="center" h="200px">
              <Spinner size="sm" color="gray.300" thickness="2px" />
            </Flex>
          ) : conversations.length === 0 ? (
            <Flex
              flex={1}
              align="center"
              justify="center"
              direction="column"
              px={4}
            >
              <Box
                w="48px"
                h="48px"
                borderRadius="full"
                bg="gray.50"
                display="flex"
                alignItems="center"
                justifyContent="center"
                mb={3}
              >
                <Icon as={BsChatDots} fontSize="20px" color="gray.300" />
              </Box>
              <Text fontSize="13px" fontWeight="500" color="gray.600" textAlign="center">
                No conversations yet
              </Text>
              <Text fontSize="12px" color="gray.400" mt={1} textAlign="center">
                {patientCanInitiate
                  ? "Start a conversation with your care team"
                  : "Your care team will message you here"
                }
              </Text>
              {patientCanInitiate && (
                <Button
                  size="sm"
                  bg="#5754FF"
                  color="white"
                  leftIcon={<FiPlus size={14} />}
                  onClick={handleNewChat}
                  mt={4}
                  fontWeight="500"
                  _hover={{ bg: "#4644e0" }}
                  _active={{ bg: "#3a38c8" }}
                >
                  New conversation
                </Button>
              )}
            </Flex>
          ) : (
            <>
              {/* Groups Section */}
              {groups.length > 0 && (
                <Box mb={1}>
                  <HStack
                    px={3}
                    py={2}
                    cursor="pointer"
                    _hover={{ bg: "gray.50" }}
                    borderRadius="md"
                    onClick={onGroupsToggle}
                    justify="space-between"
                  >
                    <HStack spacing={1.5}>
                      <Icon
                        as={isGroupsOpen ? IoChevronDown : IoChevronForward}
                        fontSize="10px"
                        color="gray.400"
                      />
                      <Text fontSize="11px" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.5px">
                        Groups
                      </Text>
                      <Text fontSize="11px" color="gray.400">
                        {groups.length}
                      </Text>
                    </HStack>
                  </HStack>

                  <Collapse in={isGroupsOpen}>
                    <VStack spacing={0} align="stretch" mt={0.5}>
                      {groups.map((group) => {
                        const isActive = selectedConversation?.conversationId === group.conversationId;
                        const unread = unreadCountFor(unreadByConversation, group.conversationId, selectedConversation?.conversationId);
                        return (
                          <HStack
                            key={group.conversationId}
                            px={3}
                            py={2}
                            cursor="pointer"
                            bg={isActive ? "#f0f0ff" : "transparent"}
                            _hover={{ bg: isActive ? "#f0f0ff" : "gray.50" }}
                            borderRadius="md"
                            onClick={() => selectConversation(group)}
                            spacing={2.5}
                            transition="background 0.1s"
                          >
                            <Box
                              w="24px"
                              h="24px"
                              borderRadius="md"
                              bg={isActive ? "#5754FF" : "gray.100"}
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              flexShrink={0}
                            >
                              <Icon as={FiHash} color={isActive ? "white" : "gray.500"} fontSize="11px" />
                            </Box>
                            <Text
                              fontSize="13px"
                              fontWeight={isActive ? "500" : "normal"}
                              color={isActive ? "#1a1f36" : "gray.700"}
                              flex={1}
                              noOfLines={1}
                            >
                              {conversationLabel(group, patient.pid)}
                            </Text>
                            {unread > 0 && (
                              <Box
                                bg="#5754FF"
                                color="white"
                                fontSize="10px"
                                fontWeight="600"
                                px={1.5}
                                py={0.5}
                                borderRadius="full"
                                minW="18px"
                                textAlign="center"
                              >
                                {formatBadgeCount(unread)}
                              </Box>
                            )}
                          </HStack>
                        );
                      })}
                    </VStack>
                  </Collapse>
                </Box>
              )}

              {/* Direct Messages Section */}
              {(dms.length > 0 || patientCanInitiate) && (
                <Box>
                  <HStack
                    px={3}
                    py={2}
                    cursor="pointer"
                    _hover={{ bg: "gray.50" }}
                    borderRadius="md"
                    onClick={onDMsToggle}
                    justify="space-between"
                  >
                    <HStack spacing={1.5} flex={1}>
                      <Icon
                        as={isDMsOpen ? IoChevronDown : IoChevronForward}
                        fontSize="10px"
                        color="gray.400"
                      />
                      <Text fontSize="11px" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="0.5px">
                        Direct Messages
                      </Text>
                      <Text fontSize="11px" color="gray.400">
                        {dms.length}
                      </Text>
                    </HStack>
                    {patientCanInitiate && (
                      <Tooltip label="New conversation" fontSize="xs" placement="top">
                        <IconButton
                          icon={<FiPlus />}
                          size="xs"
                          variant="ghost"
                          color="gray.500"
                          _hover={{ bg: "gray.100", color: "#5754FF" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleNewChat();
                          }}
                          aria-label="New conversation"
                        />
                      </Tooltip>
                    )}
                  </HStack>

                  <Collapse in={isDMsOpen}>
                    <VStack spacing={0} align="stretch" mt={0.5}>
                      {dms.length > 0 ? (
                        dms.map((conv) => {
                          const isActive = selectedConversation?.conversationId === conv.conversationId;
                          const unread = unreadCountFor(unreadByConversation, conv.conversationId, selectedConversation?.conversationId);
                          return (
                            <HStack
                              key={conv.conversationId}
                              px={3}
                              py={2}
                              cursor="pointer"
                              bg={isActive ? "#f0f0ff" : "transparent"}
                              _hover={{ bg: isActive ? "#f0f0ff" : "gray.50" }}
                              borderRadius="md"
                              onClick={() => selectConversation(conv)}
                              spacing={2.5}
                              transition="background 0.1s"
                            >
                              <CSSAvatar user={otherMemberOf(conv, patient.pid)} size="xs" />
                              <Box flex={1} minW={0}>
                                <Text
                                  fontSize="13px"
                                  fontWeight={isActive ? "500" : "normal"}
                                  color={isActive ? "#1a1f36" : "gray.700"}
                                  noOfLines={1}
                                >
                                  {conversationLabel(conv, patient.pid)}
                                </Text>
                                {conv.lastMessage && (
                                  <Text fontSize="11px" color="gray.400" noOfLines={1}>
                                    {conv.lastMessage.messageType === "file"
                                      ? "Sent a file"
                                      : conv.lastMessage.message || ""}
                                  </Text>
                                )}
                              </Box>
                              {unread > 0 && (
                                <Box
                                  bg="#5754FF"
                                  color="white"
                                  fontSize="10px"
                                  fontWeight="600"
                                  px={1.5}
                                  py={0.5}
                                  borderRadius="full"
                                  minW="18px"
                                  textAlign="center"
                                >
                                  {formatBadgeCount(unread)}
                                </Box>
                              )}
                            </HStack>
                          );
                        })
                      ) : (
                        <Box px={3} py={4}>
                          <Text fontSize="12px" color="gray.400" textAlign="center">
                            No direct messages
                          </Text>
                          {patientCanInitiate && (
                            <Button
                              size="xs"
                              variant="ghost"
                              color="#5754FF"
                              leftIcon={<FiPlus />}
                              onClick={handleNewChat}
                              mt={2}
                              w="full"
                              fontWeight="500"
                              _hover={{ bg: "#f0f0ff" }}
                            >
                              Start a conversation
                            </Button>
                          )}
                        </Box>
                      )}
                    </VStack>
                  </Collapse>
                </Box>
              )}
            </>
          )}
        </VStack>
      </Box>
      
      {/* Right Panel - Chat Area */}
      <Box flex={1} bg="white" position="relative">
        {selectedConversation ? (
          <MessagingContext.Provider value={{ schState: dashState, dispatch, outbox, storedListeners: storedListenersRef.current }}>
            <GroupManagementContext.Provider value={{ openGroupManagement }}>
              <PatientContext.Provider value={[selectedConversation, setSelectedConversation]}>
                <StaffChatArea
                  key={`${threadKey}-${selectedConversation.members?.length || 0}`}
                  user={selectedConversation}
                  onBack={() => selectConversation(null)}
                  loadConversations={refreshConversations}
                  onConversationCreated={handleConversationCreated}
                  isPatientView={true}
                  currentUserId={patient.pid}
                  canSendMessage={canPatientRespondToConversation(
                    selectedConversation,
                    patient.pid
                  )}
                />
              </PatientContext.Provider>
            </GroupManagementContext.Provider>
          </MessagingContext.Provider>
        ) : (
          <Flex h="full" align="center" justify="center" direction="column">
            <Box
              w="64px"
              h="64px"
              borderRadius="full"
              bg="gray.50"
              display="flex"
              alignItems="center"
              justifyContent="center"
              mb={4}
            >
              <Icon as={BsChatDots} fontSize="24px" color="gray.300" />
            </Box>
            <Text fontSize="15px" fontWeight="500" color="#1a1f36">
              Select a conversation
            </Text>
            <Text fontSize="13px" color="#6b7280" mt={1}>
              Choose from the sidebar to start messaging
            </Text>
          </Flex>
        )}
      </Box>
      
      {/* Group Management Modal */}
      {showGroupManagementModal && managedGroup && (
        <GroupManagementModal
          isOpen={showGroupManagementModal}
          onClose={() => {
            setShowGroupManagementModal(false);
            setManagedGroup(null);
          }}
          group={managedGroup}
          currentUserId={patient.pid}
          patient={patient}
          dashState={dashState}
          onLeaveGroup={handleLeaveGroup}
        />
      )}
      
      {/* New Chat Modal */}
      <Modal
        isOpen={showNewChatModal}
        onClose={() => setShowNewChatModal(false)}
        size="md"
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay bg="rgba(0, 0, 0, 0.3)" backdropFilter="blur(2px)" />
        <ModalContent maxH="80vh" borderRadius="xl" overflow="hidden">
          <ModalHeader
            fontSize="16px"
            fontWeight="600"
            color="#1a1f36"
            borderBottom="1px solid"
            borderColor="gray.100"
            pb={3}
            pt={5}
          >
            New conversation
          </ModalHeader>
          <ModalCloseButton top={4} />

          <ModalBody
            py={4}
            overflowY="auto"
            maxH="60vh"
            className="sbar3"
          >
            {loadingDoctors ? (
              <Flex justify="center" py={8}>
                <Spinner size="sm" color="gray.300" thickness="2px" />
              </Flex>
            ) : availableDoctors.length > 0 ? (
              <VStack spacing={2} align="stretch">
                <Text fontSize="13px" color="#6b7280" mb={2}>
                  Select a staff member to message
                </Text>

                {!availableDoctors.some(s => s.isAssigned && s.userType === 'doctor') && (
                  <Box
                    px={3}
                    py={2.5}
                    bg="gray.50"
                    borderRadius="lg"
                    borderLeft="3px solid"
                    borderLeftColor="gray.300"
                  >
                    <Text fontSize="12px" color="gray.500">
                      No primary doctor assigned
                    </Text>
                  </Box>
                )}

                {availableDoctors.filter(s => s.isAssigned && s.userType === 'doctor').length > 0 && (
                  <>
                    <Text fontSize="11px" fontWeight="600" color="gray.400" textTransform="uppercase" letterSpacing="0.5px" mt={1}>
                      Your Doctors
                    </Text>
                    {availableDoctors
                      .filter(s => s.isAssigned && s.userType === 'doctor')
                      .map((staff) => (
                        <HStack
                          key={staff.id}
                          px={3}
                          py={2.5}
                          bg="white"
                          border="1px solid"
                          borderColor={staff.isPrimary ? "green.100" : "gray.100"}
                          borderRadius="lg"
                          cursor="pointer"
                          _hover={{
                            bg: "gray.50",
                            borderColor: staff.isPrimary ? "green.200" : "gray.200"
                          }}
                          transition="all 0.15s"
                          onClick={() => handleStartConversation(staff)}
                        >
                          <CSSAvatar
                            user={{
                              name: staff.name,
                              photo: staff.photo
                            }}
                            size="sm"
                          />
                          <VStack align="start" spacing={0} flex={1}>
                            <Text fontSize="13px" fontWeight="500" color="#1a1f36">
                              {staff.name}
                            </Text>
                            <HStack spacing={2}>
                              <Text fontSize="11px" color="#6b7280">
                                {staff.specialty || 'Clinician'}
                              </Text>
                              {staff.isPrimary && (
                                <Badge
                                  bg="green.50"
                                  color="green.700"
                                  fontSize="10px"
                                  px={1.5}
                                  borderRadius="md"
                                  fontWeight="500"
                                >
                                  Primary
                                </Badge>
                              )}
                            </HStack>
                          </VStack>
                        </HStack>
                      ))
                    }
                  </>
                )}

              </VStack>
            ) : (
              <Box py={8} textAlign="center">
                <Text fontSize="13px" color="gray.500">
                  No available staff to message
                </Text>
                <Text fontSize="12px" color="gray.400" mt={1}>
                  All your assigned doctors already have conversations with you
                </Text>
              </Box>
            )}
          </ModalBody>

          <ModalFooter borderTop="1px solid" borderColor="gray.100" pt={3} pb={3}>
            <Button
              variant="ghost"
              onClick={() => setShowNewChatModal(false)}
              fontSize="13px"
              color="gray.600"
              fontWeight="500"
              _hover={{ bg: "gray.50" }}
            >
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
